import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowClockwise, ChatCircleDots, Warning, CheckCircle, XCircle, Clock, User, Trophy, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import MBSetupBadge from '../components/MBSetupBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPTION_LABELS = {
  new_id: '🆔 New ID',
  deposit_problem: '💰 Deposit',
  withdraw_problem: '💸 Withdrawal',
  website_issue: '🌐 Website/App',
  bonus_info: '🎁 Bonus/Offer',
  login_issue: '🔐 Login',
  bank_update: '🏦 Bank update',
  talk_person: '📞 Talk to person',
};

const SupportAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [mbBreakdown, setMbBreakdown] = useState(null);
  const [tab, setTab] = useState('sessions');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [analyticsRes, waitingRes, lbRes, mbRes] = await Promise.all([
        axios.get(`${API}/support/analytics`),
        axios.get(`${API}/support/waiting-alerts?threshold_minutes=5`),
        axios.get(`${API}/support/handler-leaderboard?days=7`),
        axios.get(`${API}/support/mb-breakdown`),
      ]);
      setData(analyticsRes.data);
      setWaiting(waitingRes.data);
      setLeaderboard(lbRes.data);
      setMbBreakdown(mbRes.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const summary = data?.summary || { total: 0, resolved_yes: 0, resolved_no: 0, pending: 0 };
  const sessions = data?.sessions || [];
  const byCustomer = data?.by_customer || [];
  const waitingRows = waiting?.waiting || [];

  return (
    <div className="h-screen overflow-y-auto flex flex-col bg-wa-hover" data-testid="support-analytics-page">
      {/* Header */}
      <div className="flex-shrink-0 bg-wa-primary-strong text-white px-4 py-3 flex items-center gap-3 shadow">
        <button
          onClick={() => navigate('/admin-dashboard')}
          className="p-1.5 rounded-full hover:bg-white/20"
          data-testid="back-btn"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">Support Analytics</h1>
          <p className="text-xs opacity-90">Bot sessions, resolutions & wait alerts</p>
        </div>
        <button
          onClick={load}
          className="p-1.5 rounded-full hover:bg-white/20"
          data-testid="refresh-btn"
        >
          <ArrowClockwise size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="flex-shrink-0 grid grid-cols-2 md:grid-cols-4 gap-2 p-3">
        <SummaryCard icon={<ChatCircleDots size={22} />} label="Total Sessions" value={summary.total} color="bg-wa-hover text-wa-text-primary" />
        <SummaryCard icon={<CheckCircle size={22} />} label="Resolved ✅" value={summary.resolved_yes} color="bg-wa-hover text-wa-text-primary" />
        <SummaryCard icon={<XCircle size={22} />} label="Unresolved ❌" value={summary.resolved_no} color="bg-wa-hover text-wa-text-primary" />
        <SummaryCard icon={<Clock size={22} />} label="Pending" value={summary.pending} color="bg-wa-hover text-wa-text-primary" />
      </div>

      {/* Old pending sessions warning bar */}
      {summary.old_pending > 0 && (
        <div className="flex-shrink-0 mx-3 mb-2 rounded-lg bg-amber-50 border border-amber-300 px-4 py-3 flex items-center justify-between" data-testid="old-pending-alert-banner">
          <div className="flex items-center gap-3">
            <Warning size={24} className="text-amber-600 flex-shrink-0" weight="fill" />
            <div>
              <div className="text-sm font-semibold text-wa-text-primary">
                {summary.old_pending} old pending sessions
              </div>
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/support/clear-old-sessions`);
                toast.success(`Successfully cleared ${res.data.cleared_count} old sessions`);
                load();
              } catch (e) {
                toast.error('Failed to clear old sessions');
              }
            }}
            className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600"
            data-testid="clear-old-btn"
          >
            Clear Old
          </button>
        </div>
      )}

      {/* Waiting alerts banner */}
      {waitingRows.length > 0 && (
        <div className="flex-shrink-0 mx-3 mb-2 rounded-lg bg-wa-hover border border-red-300 px-4 py-3 flex items-center gap-3" data-testid="waiting-alert-banner">
          <Warning size={24} className="text-red-600 flex-shrink-0" weight="fill" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-wa-text-primary">
              {waitingRows.length} customer{waitingRows.length > 1 ? 's' : ''} waiting 5+ min without reply
            </div>
            <div className="text-xs text-wa-text-primary">Deposit/Withdrawal chats — respond quickly!</div>
          </div>
          <button
            onClick={() => setTab('waiting')}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700"
            data-testid="view-waiting-btn"
          >
            View
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex-shrink-0 px-3 flex gap-2 border-b bg-white overflow-x-auto shadow-sm">
        {[
          { key: 'sessions', label: 'Sessions' },
          { key: 'customers', label: 'By Customer' },
          { key: 'waiting', label: `Waiting (${waitingRows.length})`, alert: waitingRows.length > 0 },
          { key: 'handlers', label: 'Handlers' },
          { key: 'mb', label: 'MB Breakdown' },
          { key: 'templates', label: 'Bot Templates' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key
                ? 'text-teal-700 border-teal-600'
                : `${t.alert ? 'text-red-600' : 'text-wa-text-secondary'} border-transparent hover:text-teal-600`
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <div className="p-8 text-center text-wa-text-secondary">Loading...</div>
      ) : (
        <div className="p-3">
          {tab === 'sessions' && <SessionsTable sessions={sessions} onRefresh={load} API={API} />}
          {tab === 'customers' && <CustomerAggregate rows={byCustomer} />}
          {tab === 'waiting' && <WaitingTable rows={waitingRows} navigate={navigate} />}
          {tab === 'handlers' && <HandlerLeaderboard data={leaderboard} />}
          {tab === 'mb' && <MBBreakdown data={mbBreakdown} />}
          {tab === 'templates' && <BotTemplatesEditor actorId={user?.id} />}
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon, label, value, color }) => (
  <div className={`rounded-lg ${color} px-3 py-2.5 shadow-sm`}>
    <div className="flex items-center gap-2 opacity-80 mb-1">
      {icon}
      <span className="text-xs font-semibold">{label}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

const StatusPill = ({ resolved }) => {
  if (resolved === true) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary font-bold">✅ Resolved</span>;
  if (resolved === false) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary font-bold">❌ Unresolved</span>;
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary font-bold">⏳ Pending</span>;
};

const SessionsTable = ({ sessions, onRefresh, API }) => {
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCids, setSelectedCids] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  if (sessions.length === 0) return <div className="text-center py-10 text-wa-text-secondary text-sm">No support sessions yet</div>;

  // Group by customer
  const byCust = {};
  sessions.forEach((s) => {
    const cid = s.customer_id || 'anon';
    if (!byCust[cid]) {
      byCust[cid] = {
        customer_id: cid,
        customer_name: s.customer_name,
        customer_phone: s.customer_phone,
        mb_setup: s.mb_setup,
        sessions: [],
      };
    }
    byCust[cid].sessions.push(s);
  });
  const rows = Object.values(byCust).map((r) => {
    r.sessions.sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));
    r.total = r.sessions.length;
    r.resolved_yes = r.sessions.filter((s) => s.resolved === true).length;
    r.pending = r.sessions.filter((s) => s.resolved === null).length;
    r.last_at = r.sessions[0]?.last_activity_at;
    return r;
  }).sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));

  const toggleSelectCustomer = (cid) => {
    setSelectedCids((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const handleClearSessions = async () => {
    if (selectedCids.size === 0) return;
    const roomIds = rows
      .filter(r => selectedCids.has(r.customer_id))
      .flatMap(r => r.sessions.map(s => s.room_id))
      .filter(Boolean);

    if (roomIds.length === 0) {
      toast.info("No active rooms found for selection.");
      return;
    }

    if (!window.confirm(`Are you sure you want to resolve sessions for the ${selectedCids.size} selected customer(s)?`)) {
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/support/danger-zone/clear-sessions`, { room_ids: roomIds });
      toast.success(`Successfully resolved ${res.data.cleared_count} sessions`);
      setSelectedCids(new Set());
      setSelectionMode(false);
      onRefresh();
    } catch (e) {
      toast.error('Failed to clear sessions');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Are you sure you want to resolve ALL pending support, deposit, and withdrawal sessions?")) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/support/danger-zone/clear-all`);
      toast.success(`Successfully resolved all ${res.data.cleared_count} pending sessions`);
      onRefresh();
    } catch (e) {
      toast.error('Failed to clear all sessions');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently DELETE all support, deposit, and withdrawal sessions from the database. This action CANNOT be undone. Proceed?")) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/support/danger-zone/delete-all`);
      toast.success(`Successfully deleted all ${res.data.deleted_count} sessions`);
      onRefresh();
    } catch (e) {
      toast.error('Failed to delete all sessions');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      {/* Danger Zone & Selection Row */}
      <div className="flex flex-col gap-2 mb-3 bg-white p-3 rounded-lg border shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Select Toggle */}
          <button
            onClick={() => {
              setSelectionMode(!selectionMode);
              setSelectedCids(new Set());
            }}
            className={`px-3 py-1.5 border rounded-lg text-xs font-bold transition-colors ${
              selectionMode 
                ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700' 
                : 'bg-wa-hover text-wa-text-primary border-wa-border hover:bg-wa-panel'
            }`}
          >
            {selectionMode ? 'Cancel Selection' : 'Select'}
          </button>

          {/* Danger Zone Actions */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-gray-500 font-bold uppercase tracking-wider text-[10px]">DANGER ZONE:</span>
            <button
              onClick={handleClearSessions}
              disabled={selectedCids.size === 0 || actionLoading}
              className="px-2.5 py-1.5 border border-red-300 text-red-600 font-bold rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              Clear Sessions
            </button>
            <button
              onClick={handleClearAll}
              disabled={actionLoading}
              className="px-2.5 py-1.5 border border-red-300 text-red-600 font-bold rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
            >
              Clear ALL
            </button>
          </div>
        </div>

        {/* Delete All Row */}
        <div className="flex justify-end border-t border-dashed pt-2">
          <button
            onClick={handleDeleteAll}
            disabled={actionLoading}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-xs transition-colors"
          >
            Delete All Sessions
          </button>
        </div>
      </div>

      <div className="bg-wa-panel rounded-lg shadow-sm divide-y" data-testid="sessions-table">
        {rows.map((r) => {
          const last = r.sessions[0];
          const lastOpt = (last?.options_selected || []).slice(-1)[0];
          return (
            <div
              key={r.customer_id}
              onClick={() => {
                if (selectionMode) {
                  toggleSelectCustomer(r.customer_id);
                } else {
                  setDetailCustomer(r);
                }
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-wa-hover text-left cursor-pointer transition-colors"
              data-testid={`session-row-${r.customer_id}`}
            >
              {/* Checkbox for selection mode */}
              {selectionMode && (
                <input
                  type="checkbox"
                  checked={selectedCids.has(r.customer_id)}
                  onChange={() => toggleSelectCustomer(r.customer_id)}
                  onClick={(e) => e.stopPropagation()} // Prevent bubble to row click
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-1 cursor-pointer"
                />
              )}

              <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                {(r.customer_name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-wa-text-primary truncate">{r.customer_name || 'Guest'}</span>
                  <MBSetupBadge value={r.mb_setup} />
                  <span className="text-[10px] text-wa-text-secondary">· {r.customer_phone}</span>
                </div>
                <div className="text-xs text-wa-text-secondary truncate">
                  {lastOpt ? `Last: ${OPTION_LABELS[lastOpt.key] || lastOpt.key}` : 'no clicks yet'}
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                  {r.total} session{r.total > 1 ? 's' : ''}
                </span>
                <span className="text-[10px] text-wa-text-secondary">{fmtDate(r.last_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
      {detailCustomer && <CustomerSessionsDetail row={detailCustomer} onClose={() => setDetailCustomer(null)} />}
    </>
  );
};

const CustomerSessionsDetail = ({ row, onClose }) => {
  const [expandedSession, setExpandedSession] = useState(row.sessions[0]?.id || null);
  const [messagesMap, setMessagesMap] = useState({});
  const [loadingRoom, setLoadingRoom] = useState(null);

  const loadRoomMessages = async (session) => {
    if (messagesMap[session.id] || !session.room_id) return;
    setLoadingRoom(session.id);
    try {
      const res = await axios.get(`${API}/chat/messages/${session.room_id}`);
      setMessagesMap((p) => ({ ...p, [session.id]: res.data || [] }));
    } catch {
      setMessagesMap((p) => ({ ...p, [session.id]: [] }));
    } finally {
      setLoadingRoom(null);
    }
  };

  useEffect(() => {
    if (expandedSession) {
      const s = row.sessions.find((x) => x.id === expandedSession);
      if (s) loadRoomMessages(s);
    }
    // eslint-disable-next-line
  }, [expandedSession]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="session-detail-modal">
      <div className="bg-wa-panel rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b bg-wa-panel border-l-4 border-[#008069] text-wa-text-primary flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
            {(row.customer_name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold truncate">{row.customer_name || 'Guest'}</span>
              <MBSetupBadge value={row.mb_setup} />
            </div>
            <div className="text-[11px] opacity-90">{row.customer_phone} · {row.total} sessions</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/20" data-testid="session-detail-close">
            <XCircle size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-wa-hover">
          {row.sessions.map((s, idx) => {
            const isOpen = expandedSession === s.id;
            const msgs = messagesMap[s.id] || [];
            return (
              <div key={s.id} className="bg-wa-panel rounded-lg border shadow-sm">
                <button
                  onClick={() => setExpandedSession(isOpen ? null : s.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-wa-hover text-left"
                >
                  <span className="text-xs font-bold text-wa-text-secondary">#{row.sessions.length - idx}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1 mb-0.5">
                      {(s.options_selected || []).slice(0, 4).map((o, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-wa-hover text-wa-text-primary rounded-full">
                          {OPTION_LABELS[o.key] || o.key}{o.sub_option ? ` → ${o.sub_option}` : ''}
                        </span>
                      ))}
                      {(s.options_selected || []).length === 0 && <span className="text-[10px] text-wa-text-secondary">no clicks</span>}
                    </div>
                    <div className="text-[10px] text-wa-text-secondary">{fmtDate(s.started_at)}</div>
                  </div>
                  <StatusPill resolved={s.resolved} />
                  <span className="text-wa-text-secondary text-xs">{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div className="border-t bg-wa-hover p-2 max-h-72 overflow-y-auto space-y-1.5">
                    {loadingRoom === s.id && msgs.length === 0 && <div className="text-center text-wa-text-secondary text-xs py-3">Loading conversation...</div>}
                    {msgs.length === 0 && loadingRoom !== s.id && <div className="text-center text-wa-text-secondary text-xs py-3">No messages yet</div>}
                    {msgs.map((m, i) => {
                      const isCust = m.sender_role === 'customer';
                      const isBot = m.sender_role === 'bot';
                      return (
                        <div key={i} className={`flex ${isCust ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] px-2 py-1.5 rounded-lg text-xs ${
                            isCust ? 'bg-wa-hover text-wa-text-primary' :
                            isBot ? 'bg-teal-50 border border-teal-200 text-teal-900' :
                            'bg-wa-panel border text-wa-text-primary'
                          }`}>
                            <div className="text-[9px] font-bold opacity-70">{isBot ? '🤖 Bot' : isCust ? row.customer_name || 'Customer' : (m.sender_name || m.sender_role)}</div>
                            <div className="whitespace-pre-wrap">{m.text || (m.image_url ? '📷 image' : m.voice_url ? '🎤 voice' : '')}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CustomerAggregate = ({ rows }) => {
  if (rows.length === 0) return <div className="text-center py-10 text-wa-text-secondary text-sm">No data yet</div>;
  return (
    <div className="bg-wa-panel rounded-lg shadow-sm overflow-x-auto" data-testid="customer-agg-table">
      <table className="w-full text-sm">
        <thead className="bg-wa-hover text-wa-text-secondary text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Customer</th>
            <th className="px-3 py-2 text-center">Sessions</th>
            <th className="px-3 py-2 text-center">Resolved</th>
            <th className="px-3 py-2 text-center">Unresolved</th>
            <th className="px-3 py-2 text-center">Pending</th>
            <th className="px-3 py-2 text-left">Options Used</th>
            <th className="px-3 py-2 text-left">Last Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const uniqueOpts = Array.from(new Set(r.options || []));
            return (
              <tr key={r.customer_id} className="border-t hover:bg-wa-hover">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">{r.customer_name || 'Guest'}</span>
                    <MBSetupBadge value={r.mb_setup} />
                  </div>
                  <div className="text-[11px] text-wa-text-secondary">{r.customer_phone}</div>
                </td>
                <td className="px-3 py-2 text-center font-bold">{r.count}</td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.resolved_yes}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.resolved_no}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.pending}</span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {uniqueOpts.slice(0, 4).map((k) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 bg-wa-hover rounded-full">
                        {OPTION_LABELS[k] || k}
                      </span>
                    ))}
                    {uniqueOpts.length > 4 && <span className="text-[10px] text-wa-text-secondary">+{uniqueOpts.length - 4}</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-[11px] text-wa-text-secondary">{fmtDate(r.last_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const WaitingTable = ({ rows, navigate }) => {
  if (rows.length === 0) return <div className="text-center py-10 text-wa-text-secondary text-sm">No customers currently waiting 5+ min 🎉</div>;
  return (
    <div className="bg-wa-panel rounded-lg shadow-sm overflow-x-auto" data-testid="waiting-table">
      <table className="w-full text-sm">
        <thead className="bg-wa-hover text-wa-text-primary text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Customer</th>
            <th className="px-3 py-2 text-left">Chat Type</th>
            <th className="px-3 py-2 text-left">Last Message</th>
            <th className="px-3 py-2 text-center">Waiting</th>
            <th className="px-3 py-2 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.room_id} className="border-t hover:bg-wa-hover">
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold">{r.customer_name || 'Customer'}</span>
                  <MBSetupBadge value={r.mb_setup} />
                </div>
                <div className="text-[11px] text-wa-text-secondary">{r.customer_phone}</div>
              </td>
              <td className="px-3 py-2">
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary font-bold capitalize">{r.chat_type}</span>
              </td>
              <td className="px-3 py-2 text-[11px] text-wa-text-primary max-w-xs truncate">"{r.preview}"</td>
              <td className="px-3 py-2 text-center">
                <span className="inline-flex items-center gap-1 text-wa-text-primary font-bold">
                  <Clock size={12} /> {r.waiting_minutes} min
                </span>
              </td>
              <td className="px-3 py-2">
                <button
                  onClick={() => navigate(`/admin-chat/${r.room_id}`)}
                  className="text-[11px] px-3 py-1 rounded-full bg-teal-600 text-white font-bold hover:bg-teal-700"
                  data-testid={`open-chat-${r.room_id}`}
                >
                  Open Chat
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HandlerLeaderboard = ({ data }) => {
  const rows = data?.leaderboard || [];
  if (rows.length === 0) return <div className="text-center py-10 text-wa-text-secondary text-sm">No activity data yet</div>;
  const medals = ['🥇', '🥈', '🥉'];
  return (
    <div className="bg-wa-panel rounded-lg shadow-sm overflow-x-auto" data-testid="handler-leaderboard">
      <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 border-b flex items-center gap-2 text-sm">
        <Trophy size={18} className="text-amber-600" weight="fill" />
        <span className="font-semibold text-wa-text-primary">Last {data?.days || 7} days performance</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-wa-hover text-wa-text-secondary text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Rank</th>
            <th className="px-3 py-2 text-left">Handler</th>
            <th className="px-3 py-2 text-center">Replies</th>
            <th className="px-3 py-2 text-center">Customers Helped</th>
            <th className="px-3 py-2 text-center">Sessions Resolved</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.handler_id} className="border-t hover:bg-wa-hover">
              <td className="px-3 py-2 text-lg">{medals[i] || `#${i + 1}`}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold">
                    {(r.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold">{r.name || 'Unknown'}</div>
                    <div className="text-[10px] text-wa-text-secondary capitalize">{(r.role || '').replace('_', ' ')}</div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.replies}</span>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.customers_helped}</span>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{r.resolved_sessions}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BotTemplatesEditor = ({ actorId }) => {
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/support/bot/templates`);
      setOptions(res.data.options || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, field, value) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/support/bot/templates`, {
        actor_id: actorId,
        options: options.map((o) => ({
          key: o.key,
          label: o.label,
          reply: o.reply,
          keywords: typeof o.keywords === 'string' ? o.keywords.split(',').map(s => s.trim()).filter(Boolean) : (o.keywords || []),
          has_sub_options: !!o.has_sub_options,
        })),
      });
      toast.success('Bot templates updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-wa-text-secondary">Loading templates...</div>;

  return (
    <div className="space-y-2" data-testid="bot-templates-editor">
      <div className="bg-wa-hover border border-blue-200 rounded-lg px-3 py-2 text-xs text-wa-text-primary">
        💡 यहाँ आप हर option का label, reply और keywords edit कर सकते हैं। Keywords comma-separated लिखें (जैसे: withdraw, payout, निकाल). Bot customer के message में इन keywords को detect करके सही option auto-highlight करेगा।
      </div>
      {options.map((opt, idx) => (
        <div key={opt.key} className="bg-wa-panel rounded-lg shadow-sm border">
          <button
            onClick={() => setExpanded(expanded === idx ? null : idx)}
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-wa-hover"
            data-testid={`tpl-${opt.key}`}
          >
            <div className="flex items-center gap-2">
              <PencilSimple size={16} className="text-teal-600" />
              <span className="font-semibold text-sm">{opt.label}</span>
              <span className="text-[10px] px-2 py-0.5 bg-wa-hover rounded-full text-wa-text-secondary">{opt.key}</span>
            </div>
            <span className="text-wa-text-secondary text-xs">{expanded === idx ? '▲' : '▼'}</span>
          </button>
          {expanded === idx && (
            <div className="px-3 pb-3 space-y-2 border-t pt-2">
              <div>
                <label className="text-[11px] font-semibold text-wa-text-secondary">Label (shown as button):</label>
                <input
                  type="text"
                  value={opt.label}
                  onChange={(e) => update(idx, 'label', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-wa-text-secondary">Reply text (sent to customer):</label>
                <textarea
                  value={opt.reply}
                  onChange={(e) => update(idx, 'reply', e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-wa-text-secondary">Keywords (comma-separated for smart detection):</label>
                <input
                  type="text"
                  value={Array.isArray(opt.keywords) ? opt.keywords.join(', ') : (opt.keywords || '')}
                  onChange={(e) => update(idx, 'keywords', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="withdraw, payout, निकाल"
                />
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="sticky bottom-0 bg-wa-panel border-t pt-2 pb-3">
        <button
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 text-white font-bold text-sm hover:bg-teal-700 disabled:opacity-50"
          data-testid="save-templates-btn"
        >
          <FloppyDisk size={18} /> {saving ? 'Saving...' : 'Save All Templates'}
        </button>
      </div>
    </div>
  );
};

const MBBreakdown = ({ data }) => {
  const sections = data?.sections || {};
  const sectionKeys = ['deposit', 'withdrawal', 'support'];
  let MB_KEYS = ['MB1', 'MB2', 'MB3', 'MB4', 'MB5', 'MB6'];
  try {
    const cached = localStorage.getItem('mb_list');
    if (cached) MB_KEYS = JSON.parse(cached);
  } catch {}
  let labels = {};
  try { labels = JSON.parse(localStorage.getItem('mb_labels') || '{}'); } catch {}
  if (!data) return <div className="text-center py-10 text-wa-text-secondary text-sm">Loading...</div>;
  return (
    <div className="space-y-3" data-testid="mb-breakdown">
      {sectionKeys.map((sec) => {
        const secData = sections[sec] || {};
        const totalClients = Object.values(secData).reduce((s, v) => s + (v?.clients || 0), 0);
        return (
          <div key={sec} className="bg-wa-panel rounded-lg shadow-sm border overflow-hidden" data-testid={`mb-breakdown-${sec}`}>
            <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-teal-50 border-b flex items-center justify-between">
              <span className="font-bold capitalize text-wa-text-primary">{sec}</span>
              <span className="text-xs text-wa-text-primary">{totalClients} clients</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-wa-hover text-wa-text-secondary text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Setup</th>
                  <th className="px-3 py-2 text-center">Clients</th>
                  <th className="px-3 py-2 text-center">Unresolved</th>
                  <th className="px-3 py-2 text-center">Waiting 5+ min</th>
                </tr>
              </thead>
              <tbody>
                {MB_KEYS.map((mb) => {
                  const s = secData[mb] || { clients: 0, unresolved_sessions: 0, waiting_over_5min: 0 };
                  return (
                    <tr key={mb} className="border-t hover:bg-wa-hover">
                      <td className="px-3 py-2">
                        <span className="text-[11px] font-mono px-2 py-0.5 bg-wa-hover rounded-full text-wa-text-primary mr-1.5">{mb}</span>
                        {labels[mb] && labels[mb] !== mb && <span className="text-xs text-wa-text-secondary">{labels[mb]}</span>}
                      </td>
                      <td className="px-3 py-2 text-center">{s.clients}</td>
                      <td className="px-3 py-2 text-center">
                        {s.unresolved_sessions > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{s.unresolved_sessions}</span>
                        ) : (
                          <span className="text-wa-text-secondary">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {s.waiting_over_5min > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full bg-wa-hover text-wa-text-primary text-xs font-bold">{s.waiting_over_5min}</span>
                        ) : (
                          <span className="text-wa-text-secondary">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default SupportAnalytics;
